import axios from 'axios';
// import { RootState } from '../app/store'; // To potentially access token from Redux state

// Use Create React App's way of accessing environment variables
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8001/api';

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  headers: {
    'Accept': 'application/json',
    'Content-Type': 'application/json',
    'X-Requested-With': 'XMLHttpRequest',
  },
});

// Interceptor to add the auth token and session token to requests
// This assumes you store your token in Redux state under `auth.token`
apiClient.interceptors.request.use(
  (config) => {
    // Example: Get token from localStorage or Redux store
    // const token = localStorage.getItem('authToken');
    // Or, if using Redux:
    // const state: RootState = store.getState(); // You'd need to import your store here
    // const token = state.auth.token;

    const token = localStorage.getItem('approverAuthToken'); // Using localStorage for simplicity here
    const sessionToken = localStorage.getItem('approverSessionToken'); // Session token for tracking

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    if (sessionToken) {
      config.headers['X-Session-Token'] = sessionToken;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Example of API functions (to be expanded)

/**
 * Login a user.
 * You will need to create a login route in your Laravel api.php if not already present,
 * e.g., Route::post('/login', [AuthController::class, 'login']);
 * This route should return a token on successful login.
 */
export const login = async (credentials: { email: string; password: string }) => {
  // Laravel Sanctum typically uses a CSRF cookie for SPA authentication.
  // For token-based auth, ensure your Laravel backend issues a token.
  // First, get CSRF cookie if using Sanctum SPA auth with cookies
  // await axios.get(process.env.REACT_APP_SANCTUM_CSRF_URL || 'http://localhost:8001/sanctum/csrf-cookie');

  // Then make the login request
  const response = await apiClient.post('/login', credentials); // Adjust '/login' if your route is different
  if (response.data.token) {
    localStorage.setItem('approverAuthToken', response.data.token);
    // Store session token for activity tracking
    if (response.data.session_token) {
      localStorage.setItem('approverSessionToken', response.data.session_token);
    }
    // You might also want to store user data
    if (response.data.user) {
        localStorage.setItem('approverUser', JSON.stringify(response.data.user));
    }
  }
  return response.data; // Contains token, session_token and possibly user data
};

export const logout = async (sessionToken?: string | null) => {
  try {
    // Attempt to invalidate the token on the server side.
    // The auth interceptor will add the current token to this request.
    const payload: any = {};
    if (sessionToken) {
      payload.session_token = sessionToken;
    }
    await apiClient.post('/logout', payload);
    // No specific response data is usually needed from /logout,
    // but you can add error handling if the server call fails.
  } catch (error) {
    // Log the error or handle it as needed, but proceed with local cleanup.
    console.error('API logout failed, proceeding with local cleanup:', error);
  }
  // Always remove local token and user data regardless of API call success.
  localStorage.removeItem('approverAuthToken');
  localStorage.removeItem('approverSessionToken');
  localStorage.removeItem('approverUser');
  // Optionally, you might want to clear other related local storage items or caches.
};

/**
 * Update session activity (heartbeat)
 */
export const updateActivity = async (sessionToken: string) => {
  try {
    await apiClient.post('/activity', { session_token: sessionToken });
  } catch (error) {
    console.error('Failed to update activity:', error);
    // Don't throw error as this is a background operation
  }
};

/**
 * Check if user is already authenticated in main Laravel app
 */
export const checkAuth = async () => {
  try {
    const response = await apiClient.get('/check-auth');

    // If user is authenticated, save the tokens and user data
    if (response.data.authenticated && response.data.token) {
      localStorage.setItem('approverAuthToken', response.data.token);

      if (response.data.session_token) {
        localStorage.setItem('approverSessionToken', response.data.session_token);
      }

      if (response.data.user) {
        localStorage.setItem('approverUser', JSON.stringify(response.data.user));
      }
    }

    return response.data; // Contains { authenticated: boolean, token?, user?, session_token?, message }
  } catch (error) {
    console.error('Check auth failed:', error);
    // Clear any existing tokens if check fails
    localStorage.removeItem('approverAuthToken');
    localStorage.removeItem('approverSessionToken');
    localStorage.removeItem('approverUser');
    throw error;
  }
};

// --- Impersonation API Calls ---

export interface ImpersonationUser {
  id: number;
  name: string;
  email: string;
}

export interface ImpersonationState {
  impersonating: boolean;
  user?: ImpersonationUser;
  impersonated_user?: ImpersonationUser;
  original_user?: ImpersonationUser;
}

/**
 * Get list of users available for impersonation
 */
export const getImpersonationUsers = async (): Promise<ImpersonationUser[]> => {
  const response = await apiClient.get('/impersonate/users');
  return response.data.users;
};

/**
 * Get current impersonation status
 */
export const getImpersonationStatus = async (): Promise<ImpersonationState> => {
  const response = await apiClient.get('/impersonate/status');
  return response.data;
};

/**
 * Get current impersonation state
 */
export const getCurrentImpersonation = async (): Promise<ImpersonationState> => {
  const response = await apiClient.get('/impersonate/current');
  return response.data;
};

/**
 * Start impersonating a user
 */
export const startImpersonation = async (userId: number): Promise<ImpersonationState> => {
  const response = await apiClient.post('/impersonate/start', { user_id: userId });
  return {
    impersonating: true,
    user: response.data.user,
  };
};

/**
 * Stop impersonating
 */
export const stopImpersonation = async (): Promise<void> => {
  await apiClient.post('/impersonate/stop');
};

// --- Requisition API Calls ---

interface FetchRequisitionsParams {
  status?: 'pending_my_approval' | 'all_from_my_stores';
  date_from?: string;
  date_to?: string;
  page?: number;
  // Add other potential filter params here
}

export const fetchRequisitions = async (params: FetchRequisitionsParams) => {
  const response = await apiClient.get('/approver/requisitions', { params });
  return response.data; // Expected to be paginated data
};

export const fetchRequisitionById = async (id: number | string) => {
  const response = await apiClient.get(`/approver/requisitions/${id}`);
  return response.data;
};

export const approveRequisitionApi = async (id: number | string) => {
  const response = await apiClient.post(`/approver/requisitions/${id}/approve`);
  return response.data;
};

interface RejectRequisitionPayload {
  comment?: string;
}

export const rejectRequisitionApi = async (id: number | string, payload: RejectRequisitionPayload) => {
  const response = await apiClient.post(`/approver/requisitions/${id}/reject`, payload);
  return response.data;
};

// --- Dashboard API Calls ---
export interface DashboardStats {
  pendingMyApprovalCount: number;
  recentlyApprovedCount: number;
  pendingMyApprovalValue: number; // Assuming this is a numerical value
}

export const fetchDashboardStatsApi = async (): Promise<DashboardStats> => {
  const response = await apiClient.get('/approver/dashboard-stats');
  return response.data;
};

// --- Filter Options API Calls ---

// Interface for store API response
export interface StoreApiResponse {
  id: number;
  name: string;
  storenumber?: string;
}

// Interface for category API response
export interface CategoryApiResponse {
  id: number;
  name: string;
}

// Interface for area coach API response
export interface AreaCoachApiResponse {
  id: number;
  name: string;
  user_role_id: number;
}

// Fetch stores for dropdown filter
export const fetchStoresApi = async (): Promise<StoreApiResponse[]> => {
  const response = await apiClient.get('/approver/stores');
  return response.data.data; // Extract the actual array from the nested data property
};

// Fetch categories for dropdown filter
export const fetchCategoriesApi = async (): Promise<CategoryApiResponse[]> => {
  const response = await apiClient.get('/approver/categories');
  return response.data.data; // Extract the actual array from the nested data property
};

// Fetch area coaches for dropdown filter
export const fetchAreaCoachesApi = async (): Promise<AreaCoachApiResponse[]> => {
  const response = await apiClient.get('/approver/area-coaches');
  
  // Check if response.data is an array directly (no nested data property)
  if (Array.isArray(response.data)) {
    return response.data;
  }
  
  // Otherwise, try to extract from data property if it exists
  return Array.isArray(response.data.data) ? response.data.data : [];
};

export default apiClient;
